/* oxlint-disable eslint/no-console */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    none: 4,
};

class Logger {
    private level: LogLevel;

    constructor() {
        // Default level based on environment
        // Production = warn (shows warn + error), Development = debug (shows all)
        // This can be overridden by setting window.__LOG_LEVEL__ in the browser
        this.level = import.meta.env.PROD ? 'warn' : 'debug';
    }

    private getEffectiveLevel(): LogLevel {
        if (typeof window !== 'undefined' && (window as unknown as { __LOG_LEVEL__?: LogLevel }).__LOG_LEVEL__) {
            return (window as unknown as { __LOG_LEVEL__: LogLevel }).__LOG_LEVEL__;
        }
        return this.level;
    }

    private shouldLog(level: LogLevel): boolean {
        const effectiveLevel = this.getEffectiveLevel();
        return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[effectiveLevel];
    }

    private formatMessage(namespace: string | null, args: unknown[]): unknown[] {
        if (namespace) {
            return [`[${namespace}]`, ...args];
        }
        return args;
    }

    debug(namespace: string | null, ...args: unknown[]): void {
        if (this.shouldLog('debug')) {
            console.log(...this.formatMessage(namespace, args));
        }
    }

    info(namespace: string | null, ...args: unknown[]): void {
        if (this.shouldLog('info')) {
            console.info(...this.formatMessage(namespace, args));
        }
    }

    warn(namespace: string | null, ...args: unknown[]): void {
        if (this.shouldLog('warn')) {
            console.warn(...this.formatMessage(namespace, args));
        }
    }

    error(namespace: string | null, ...args: unknown[]): void {
        if (this.shouldLog('error')) {
            console.error(...this.formatMessage(namespace, args));
        }
    }

    setLevel(level: LogLevel): void {
        this.level = level;
    }
}

export const logger = new Logger();
